import React, { useState, useEffect } from "react";
import TitleBar from "../../layout/TitleBar";
import Layout from "../../layout/Layout";
import Alert from "../../layout/Alert";
import { setAlert } from "../../../actions/alert";
import { useDispatch, useSelector } from "react-redux";
import setting_file from "../../../utils/settings.json";
import { updateSettings, getAdminSetting } from "../../../actions/admin/settings";
import { EyeIcon, EyeOffIcon } from "../../icons";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import useSaveConfirmation from "../components/useSaveConfirmation";
import {
  ChangeList,
  diffFields,
  asYen,
  asOnOff,
  asText,
  withUnit,
} from "../components/ChangeSummary";

const CryptoJS = require("crypto-js");
const CRYPTO_SECRET = setting_file.CRYPTO_SECRET;

/**
 * A focused `<input type="number">` treats the mouse wheel as a value change, so
 * scrolling the page with the cursor over the field silently edits it -- easy to
 * do, and easy not to notice, on a form where the number is a live setting.
 *
 * Dropping focus hands the gesture back to the page, which is what the user meant
 * by it. Bound once on the <form> so it covers every number field on this screen,
 * including any added later.
 */
const blurNumberInputOnWheel = (e) => {
  const el = e.target;
  if (el && el.type === "number" && el === document.activeElement) {
    el.blur();
  }
};

// Settings load asynchronously, and an unsaved price is legitimately 0, so an
// empty string is used only for "not loaded / never configured" -- keeping the
// inputs controlled without turning a real 0 into a blank field.
const priceField = (loading, value) =>
  loading || value === undefined || value === null ? "" : String(value);

const decryptSmtpPass = (encrypted) => {
  if (!encrypted) return "";

  // A password encrypted under a different secret decrypts to an empty string and
  // then blows up in JSON.parse, taking the whole settings screen down with it.
  // A password that cannot be read is treated as "not set" -- the admin retypes it.
  try {
    const plain = CryptoJS.AES.decrypt(encrypted, CRYPTO_SECRET).toString(
      CryptoJS.enc.Utf8
    );
    return plain ? JSON.parse(plain) : "";
  } catch (err) {
    return "";
  }
};

// The saved document as the form sees it. Used both to fill the form and, on
// submit, as the "before" side of the change summary, so the two can never drift.
const settingToFormData = (setting, loading) => ({
  id: loading || !setting?._id ? "" : setting?._id,
  buffertime: loading || !setting?.buffertime ? "" : setting?.buffertime,
  carttimeout: loading || !setting?.carttimeout ? "" : setting?.carttimeout,
  totaltriptime: loading || !setting?.totaltriptime ? "" : setting?.totaltriptime,
  smtpemail: loading || !setting?.smtpemail ? "" : setting?.smtpemail,
  smtppass: loading ? "" : decryptSmtpPass(setting?.smtppass),
  // Never configured reads as on, matching the server-side default.
  assignmentConflictCheck: setting?.assignmentConflictCheck !== false,
  // 0 is a legitimate price ("service offered free"), so these check for a
  // missing value rather than a falsy one.
  meetAssistBasePrice: priceField(loading, setting?.meetAssistBasePrice),
  vipMeetAssistBasePrice: priceField(loading, setting?.vipMeetAssistBasePrice),
  vipMeetAssistAdditionalPrice: priceField(
    loading,
    setting?.vipMeetAssistAdditionalPrice
  ),
});

/**
 * Every setting this form writes, in the order it appears on screen, with the label
 * and formatter the change summary shows it under.
 *
 * The summary is the point of the confirmation: these values are site-wide, apply the
 * moment they are saved, and nothing on the customer site announces that they changed.
 */
const SETTING_FIELDS = [
  { name: "buffertime", label: "Booking lead time", format: withUnit("hours") },
  { name: "carttimeout", label: "Cart timeout", format: withUnit("minutes") },
  { name: "totaltriptime", label: "Max total trip time", format: withUnit("hours") },
  { name: "meetAssistBasePrice", label: "Meet & Assist", format: asYen },
  {
    name: "vipMeetAssistBasePrice",
    label: "VIP Meet & Assist, first passenger",
    format: asYen,
  },
  {
    name: "vipMeetAssistAdditionalPrice",
    label: "VIP Meet & Assist, each additional passenger",
    format: asYen,
  },
  { name: "assignmentConflictCheck", label: "Prevent double booking", format: asOnOff },
  { name: "smtpemail", label: "SMTP email", format: asText },
  {
    name: "smtppass",
    label: "SMTP password",
    // The password is never rendered, not even the saved one -- the admin only needs
    // to know that this is one of the things about to change.
    format: (value) => (value ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : "Not set"),
  },
];

const Settings = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.setting);
  const { setting, loading } = settings;
  const [formData, setFormData] = useState({
    id: "",
    buffertime: "",
    carttimeout: "10",
    totaltriptime: "",
    smtpemail: "",
    smtppass: "",
    assignmentConflictCheck: true,
    meetAssistBasePrice: "",
    vipMeetAssistBasePrice: "",
    vipMeetAssistAdditionalPrice: "",
  });

  useEffect(() => {
    dispatch(getAdminSetting());
  }, []);

  useEffect(() => {
    setFormData(settingToFormData(setting, loading));
  }, [setting]);

  const {
    buffertime,
    carttimeout,
    totaltriptime,
    smtpemail,
    smtppass,
    assignmentConflictCheck,
    meetAssistBasePrice,
    vipMeetAssistBasePrice,
    vipMeetAssistAdditionalPrice,
  } = formData;

  const [showPassword, setShowPassword] = useState(false);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const save = useSaveConfirmation({
    save: () =>
      dispatch(
        updateSettings({
          id: formData.id,
          buffertime: formData.buffertime,
          carttimeout: formData.carttimeout,
          totaltriptime: formData.totaltriptime,
          smtpemail: formData.smtpemail,
          smtppass: CryptoJS.AES.encrypt(
            JSON.stringify(formData.smtppass),
            CRYPTO_SECRET
          ).toString(),
          assignmentConflictCheck: formData.assignmentConflictCheck,
          meetAssistBasePrice: formData.meetAssistBasePrice,
          vipMeetAssistBasePrice: formData.vipMeetAssistBasePrice,
          vipMeetAssistAdditionalPrice: formData.vipMeetAssistAdditionalPrice,
        })
      ),
  });

  // Submitting no longer writes anything: it works out what would change and asks.
  const onSubmit = (e) => {
    e.preventDefault();

    const changes = diffFields(
      SETTING_FIELDS,
      settingToFormData(setting, loading),
      formData
    );

    if (changes.length === 0) {
      dispatch(setAlert("Nothing to update -- no settings were changed.", "primary"));
      return;
    }

    save.request(changes);
  };

  return (
    <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
          <TitleBar title="Update site settings" />
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-5">
                <form onSubmit={(e) => onSubmit(e)} onWheel={blurNumberInputOnWheel}>

                  <div className="card mb-3">
                    <div className="card-header">Settings for bookings</div>
                    <div className="card-body">

                      <div className="mb-3">
                        <label className="label mb-2">
                          Booking Lead Time : [hours]
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="text"
                            className="form-control form-control-admin"
                            placeholder="Enter buffer time"
                            name="buffertime"
                            value={buffertime}
                            onChange={(e) => onChange(e)}
                            required
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="label mb-2">
                          Cart timeout : [minutes]
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="number"
                            min="1"
                            max="1440"
                            step="1"
                            className="form-control form-control-admin"
                            placeholder="Enter timeout"
                            name="carttimeout"
                            value={carttimeout}
                            onChange={(e) => onChange(e)}
                            required
                          />
                          <small className="text-muted">
                            How long a customer's cart is held. Drives the countdown shown on the
                            cart and checkout pages; once it lapses the bookings move to Abandoned
                            Carts. Takes effect within a minute, on carts already open as well as
                            new ones.
                          </small>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="label mb-2">
                          Max total trip time : [hours]
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="text"
                            className="form-control form-control-admin"
                            placeholder="Enter trip time"
                            name="totaltriptime"
                            value={totaltriptime}
                            onChange={(e) => onChange(e)}
                            required
                          />
                        </div>
                      </div>

                    </div>
                  </div>

                  <div className="card mb-3">
                    <div className="card-header">Settings for Meet &amp; Assist</div>
                    <div className="card-body">

                      {/* <p className="text-muted small">
                        Base prices in yen, before the night surcharge. Pickups and dropoffs
                        between 22:00 and 06:00 Japan time are automatically charged 1.25&times;
                        the price set here, rounded up. Applies to new bookings only &mdash;
                        bookings already placed keep the price they were quoted.
                      </p> */}

                      <div className="mb-3">
                        <label className="label mb-2" htmlFor="meetAssistBasePrice">
                          Meet &amp; Assist : 
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            id="meetAssistBasePrice"
                            className="form-control form-control-admin"
                            placeholder="Enter price"
                            name="meetAssistBasePrice"
                            value={meetAssistBasePrice}
                            onChange={(e) => onChange(e)}
                            required
                          />
                          <small className="text-muted">
                            Flat price per leg, regardless of how many passengers travel.
                          </small>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="label mb-2" htmlFor="vipMeetAssistBasePrice">
                          VIP Meet &amp; Assist, first passenger : 
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            id="vipMeetAssistBasePrice"
                            className="form-control form-control-admin"
                            placeholder="Enter price"
                            name="vipMeetAssistBasePrice"
                            value={vipMeetAssistBasePrice}
                            onChange={(e) => onChange(e)}
                            required
                          />
                          <small className="text-muted">
                            Charged once per leg, covering the first passenger.
                          </small>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="label mb-2" htmlFor="vipMeetAssistAdditionalPrice">
                          VIP Meet &amp; Assist, each additional passenger : 
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            id="vipMeetAssistAdditionalPrice"
                            className="form-control form-control-admin"
                            placeholder="Enter price"
                            name="vipMeetAssistAdditionalPrice"
                            value={vipMeetAssistAdditionalPrice}
                            onChange={(e) => onChange(e)}
                            required
                          />
                          <small className="text-muted">
                            Charged for every passenger beyond the first.
                          </small>
                        </div>
                      </div>

                    </div>
                  </div>

                  <div className="card mb-3">
                    <div className="card-header">Settings for vehicle &amp; driver assignment</div>
                    <div className="card-body">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="assignmentConflictCheck"
                          name="assignmentConflictCheck"
                          checked={assignmentConflictCheck}
                          onChange={(e) =>
                            setFormData({ ...formData, assignmentConflictCheck: e.target.checked })
                          }
                        />
                        <label className="form-check-label" htmlFor="assignmentConflictCheck">
                          Prevent double booking
                        </label>
                      </div>
                      <small className="text-muted">
                        When on, a vehicle or driver already assigned to another booking at the same
                        time cannot be picked, and the assignment is rejected if it is attempted.
                      </small>
                    </div>
                  </div>

                  <div className="card mb-3">
                    <div className="card-header">Settings for connected email account</div>
                    <div className="card-body">

                      <div className="mb-3">
                        <label className="label mb-2">
                          smtp email : [gmail account]
                        </label>
                        <div className="col-sm-12">
                          <input
                            type="text"
                            className="form-control form-control-admin"
                            placeholder="Enter trip time"
                            name="smtpemail"
                            value={smtpemail}
                            onChange={(e) => onChange(e)}
                            required
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="label mb-2">smtp email password:</label>
                        <div className="col-sm-12 position-relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control form-control-admin"
                            placeholder="Enter smtp password"
                            name="smtppass"
                            value={smtppass}
                            onChange={(e) => onChange(e)}
                            style={{ paddingRight: '45px' }}
                            required
                          />
                          <div 
                            onClick={togglePasswordVisibility}
                            style={{ 
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'pointer',
                              zIndex: 5,
                              display: 'flex',
                              alignItems: 'center',
                              padding: '4px'
                            }}
                          >
                            {showPassword ? (
                              <EyeIcon />
                            ) : (
                              <EyeOffIcon />
                            )}
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="col-sm-offset-2 col-sm-10">
                      <button type="submit" className="btn btn-primary">
                        <i
                          className="fa fa-plus-circle fa-fw"
                          aria-hidden="true"
                        ></i>{" "}
                        Update
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <Alert />

          <ConfirmationModal
            show={!!save.summary}
            title="Apply these setting changes?"
            description="These apply site-wide the moment they are saved, and affect every new booking from then on."
            confirmLabel="Update settings"
            confirmClicks={3}
            busyLabel="Saving..."
            isBusy={save.isSaving}
            error={save.error}
            onCancel={save.cancel}
            onConfirm={save.confirm}
          >
            <ChangeList changes={save.summary || []} />
          </ConfirmationModal>

          <SuccessModal
            show={!!save.saved}
            title="Settings updated"
            description={`${(save.saved?.length ?? 0) === 1
              ? "This change is"
              : `These ${save.saved?.length ?? 0} changes are`
              } live now. Bookings already placed keep the values they were quoted.`}
            onClose={save.dismiss}
          >
            <ChangeList changes={save.saved || []} />
          </SuccessModal>
      </main>
    </Layout>
  );
};

export default Settings;
